import React from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Calendar, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface ScheduleValue {
  start: string | null;
  end: string | null;
}

interface ScheduleCellProps {
  value: ScheduleValue;
  onChange: (value: ScheduleValue) => void;
  disabled?: boolean;
}

/**
 * Converts a Date object to Meta API format: YYYY-MM-DDTHH:MM:SS+HHMM
 * Preserves local timezone offset instead of converting to UTC.
 */
export function toMetaDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Get timezone offset in minutes and convert to +HHMM format
  const offsetMinutes = -date.getTimezoneOffset();
  const offsetSign = offsetMinutes >= 0 ? "+" : "-";
  const offsetHours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, "0");
  const offsetMins = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${offsetHours}${offsetMins}`;
}

/**
 * Converts a Meta date string or ISO string to datetime-local input format (YYYY-MM-DDTHH:MM)
 * Handles various formats including:
 * - ISO with T separator: 2024-01-25T10:30:00+0200
 * - Space separator: 2024-01-25 10:30:00+02
 * - With/without timezone, with/without milliseconds
 */
function toDatetimeLocalValue(metaDateString: string | null): string {
  if (!metaDateString) return "";

  // Normalize: replace space with T for consistent parsing
  const normalized = metaDateString.replace(" ", "T");

  // Extract YYYY-MM-DDTHH:MM from the normalized string
  const match = normalized.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  if (match) {
    return match[1];
  }

  // Fallback: try parsing as Date and formatting
  try {
    const date = new Date(metaDateString);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  } catch {
    // Ignore parsing errors
  }

  return "";
}

/**
 * Formats a date for display (e.g., "Jan 25")
 */
function formatDisplayDate(metaDateString: string | null): string | null {
  if (!metaDateString) return null;
  try {
    const date = new Date(metaDateString);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export function ScheduleCell({ value, onChange, disabled }: ScheduleCellProps) {
  const [open, setOpen] = useState(false);
  // Local state for pending changes
  const [localStart, setLocalStart] = useState<string | null>(value.start);
  const [localEnd, setLocalEnd] = useState<string | null>(value.end);

  // Sync local state when popover opens or value changes externally
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setLocalStart(value.start);
      setLocalEnd(value.end);
    }
    setOpen(isOpen);
  };

  const startDisplay = formatDisplayDate(value.start);
  const endDisplay = formatDisplayDate(value.end);

  // Build display text
  let displayText = "Set Schedule";
  if (startDisplay && endDisplay) {
    displayText = `${startDisplay} - ${endDisplay}`;
  } else if (startDisplay) {
    displayText = `${startDisplay} →`;
  } else if (endDisplay) {
    displayText = `→ ${endDisplay}`;
  }

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    console.log("handleStartChange - input value:", inputValue);
    if (inputValue) {
      const date = new Date(inputValue);
      const metaDate = toMetaDateString(date);
      console.log("Converted to Meta format:", metaDate);
      setLocalStart(metaDate);
    } else {
      setLocalStart(null);
    }
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    console.log("handleEndChange - input value:", inputValue);
    if (inputValue) {
      const date = new Date(inputValue);
      const metaDate = toMetaDateString(date);
      console.log("Converted to Meta format:", metaDate);
      setLocalEnd(metaDate);
    } else {
      setLocalEnd(null);
    }
  };

  const clearStart = () => {
    setLocalStart(null);
  };

  const clearEnd = () => {
    setLocalEnd(null);
  };

  const handleApply = () => {
    console.log("ScheduleCell handleApply called");
    console.log("localStart:", localStart);
    console.log("localEnd:", localEnd);
    console.log("Calling onChange with:", { start: localStart, end: localEnd });
    onChange({ start: localStart, end: localEnd });
    setOpen(false);
  };

  const handleClear = () => {
    onChange({ start: null, end: null });
    setOpen(false);
  };

  const hasValue = value.start || value.end;
  const hasLocalChanges = localStart !== value.start || localEnd !== value.end;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          disabled={disabled}
          className={cn(
            "flex items-center gap-1 w-full h-8 px-2 text-sm rounded",
            "border border-transparent transition-colors cursor-pointer",
            "hover:border-border focus:border-primary focus:outline-none",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            !hasValue && "text-muted-foreground/70"
          )}
        >
          <Calendar className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{displayText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <h4 className="font-medium text-sm">Ad Schedule</h4>

          {/* Start Time */}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">Start Time</label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={toDatetimeLocalValue(localStart)}
                onChange={handleStartChange}
                className={cn(
                  "flex-1 h-9 px-3 text-sm bg-background border rounded-md",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearStart}
                disabled={!localStart}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* End Time */}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">End Time</label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={toDatetimeLocalValue(localEnd)}
                onChange={handleEndChange}
                className={cn(
                  "flex-1 h-9 px-3 text-sm bg-background border rounded-md",
                  "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-muted-foreground hover:text-foreground"
                onClick={clearEnd}
                disabled={!localEnd}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={!hasValue && !localStart && !localEnd}
              className="text-muted-foreground"
            >
              Clear
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
